/**
 * @file       Currency format helpers for $nn,nnn.nn
 * @desc       Parse and format money text used by appCurrencyInput.
 */

/**
 * @desc   - Insert commas into an integer digit string
 * @access - private
 * @param  - digits : integer digits only
 * @return - grouped integer text
 */
function groupThousands
(
    digits: string
): string
{
    let result;
    let i;
    let offset;
    let ch;

    result = '';
    offset = 0;

    if (digits)
    {
        offset = digits.length % 3;

        for (i = 0; i < digits.length; i++)
        {
            ch = digits.charAt(i);

            if (i > 0 && (i - offset) % 3 === 0)
            {
                result = result + ',';
            }

            result = result + ch;
        }
    }

    return result;
}

/**
 * @desc   - Format raw text as $nn,nnn.nn
 * @access - public
 * @param  - value : raw or display amount
 * @param  - complete : pad cents when true
 * @return - currency string or empty
 */
function formatCurrencyInput
(
    value: string | number | null | undefined,
    complete: boolean
): string
{
    let result;
    let source;
    let i;
    let ch;
    let integers;
    let decimals;
    let hasDot;
    let grouped;
    let stripped;

    result = '';
    source = '';
    integers = '';
    decimals = '';
    hasDot = false;
    grouped = '';
    stripped = '';

    if (value !== null && value !== undefined)
    {
        source = String(value);
    }

    for (i = 0; i < source.length; i++)
    {
        ch = source.charAt(i);

        if (ch === '.' && !hasDot)
        {
            hasDot = true;
        }
        else if (ch >= '0' && ch <= '9')
        {
            if (hasDot)
            {
                if (decimals.length < 2)
                {
                    decimals = decimals + ch;
                }
            }
            else if (integers.length < 9)
            {
                integers = integers + ch;
            }
        }
    }

    stripped = integers.replace(/^0+/, '');

    if (stripped === '' && integers.length > 0)
    {
        integers = '0';
    }
    else
    {
        integers = stripped;
    }

    if (integers === '' && hasDot)
    {
        integers = '0';
    }

    if (integers !== '')
    {
        grouped = groupThousands(integers);
        result = '$' + grouped;

        if (complete)
        {
            if (decimals.length === 0)
            {
                decimals = '00';
            }
            else if (decimals.length === 1)
            {
                decimals = decimals + '0';
            }

            result = result + '.' + decimals;
        }
        else if (hasDot)
        {
            result = result + '.' + decimals;
        }
    }

    return result;
}

/**
 * @desc   - Strip currency text to a two-decimal string
 * @access - public
 * @param  - value : display or raw amount
 * @return - two-decimal string or empty
 */
function parseCurrencyAmount
(
    value: string | number | null | undefined
): string
{
    let result;
    let cleaned;
    let amount;

    result = '';

    if (value !== null && value !== undefined &&
        value !== '')
    {
        cleaned = String(value).replace(/[^0-9.]/g, '');
        amount = parseFloat(cleaned);

        if (!isNaN(amount))
        {
            result = amount.toFixed(2);
        }
    }

    return result;
}

/**
 * @desc   - Strip currency text to a number
 * @access - public
 * @param  - value : display or raw amount
 * @return - number or null
 */
function parseCurrencyNumber
(
    value: string | number | null | undefined
): number | null
{
    let result;
    let parsed;

    result = null;
    parsed = parseCurrencyAmount(value);

    if (parsed !== '')
    {
        result = Number(parsed);
    }

    return result;
}

export
{
    formatCurrencyInput,
    parseCurrencyAmount,
    parseCurrencyNumber
};
